/**
 * BMP cart drawer upsell ("Pairs great with") — carousel arrows.
 *
 * Markup comes from snippets/bmp-cart-upsell.liquid, which is re-rendered by
 * Section Rendering morphs on every cart change. Scripts inside that subtree
 * never re-execute, so this module is loaded once from snippets/cart-drawer.liquid
 * and uses delegated listeners on document.
 *
 * - Arrows scroll the track by most of its visible width and are disabled at
 *   the start / end of the track (like Sloth Hiking Club).
 * - Safety net: on a product page, hides the card for the product being viewed
 *   (Liquid already excludes it, but cart re-renders can lose page context).
 */

const SCROLL = '[data-bmp-upsell-scroll]';
const TRACK = '[data-bmp-upsell-track]';
const ROOT = '[data-bmp-cart-upsell]';
const CARD = '[data-bmp-upsell-product-id]';
const EDGE_TOLERANCE = 2;

function currentProductId() {
  const id = /** @type {any} */ (window).ShopifyAnalytics?.meta?.product?.id;
  return id ? String(id) : '';
}

/** @param {Element} root */
function hideCurrentProduct(root) {
  const id = currentProductId();
  if (!id) return;
  for (const card of root.querySelectorAll(CARD)) {
    if (card instanceof HTMLElement && card.dataset.bmpUpsellProductId === id && !card.hidden) {
      card.hidden = true;
    }
  }
}

/** @param {Element} root */
function updateArrows(root) {
  const track = root.querySelector(TRACK);
  if (!(track instanceof HTMLElement)) return;

  const maxScroll = track.scrollWidth - track.clientWidth;
  const atStart = track.scrollLeft <= EDGE_TOLERANCE;
  const atEnd = maxScroll <= EDGE_TOLERANCE || track.scrollLeft >= maxScroll - EDGE_TOLERANCE;

  for (const arrow of root.querySelectorAll(SCROLL)) {
    if (!(arrow instanceof HTMLButtonElement)) continue;
    const dir = Number(arrow.dataset.bmpUpsellScroll);
    const disabled = dir < 0 ? atStart : atEnd;
    if (arrow.disabled !== disabled) arrow.disabled = disabled;
  }
}

function updateAll() {
  for (const root of document.querySelectorAll(ROOT)) {
    hideCurrentProduct(root);
    updateArrows(root);
  }
}

let rafId = 0;
function scheduleUpdate() {
  if (rafId) return;
  rafId = requestAnimationFrame(() => {
    rafId = 0;
    updateAll();
  });
}

if (!window.__bmpCartUpsellInit) {
  window.__bmpCartUpsellInit = true;

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const arrow = target?.closest(SCROLL);
    if (!(arrow instanceof HTMLButtonElement) || arrow.disabled) return;

    const root = arrow.closest(ROOT);
    const track = root?.querySelector(TRACK);
    if (!(track instanceof HTMLElement)) return;

    const dir = Number(arrow.dataset.bmpUpsellScroll) || 1;
    track.scrollBy({ left: dir * track.clientWidth * 0.8, behavior: 'smooth' });
  });

  // Scroll doesn't bubble, so listen in the capture phase for track scrolls.
  document.addEventListener(
    'scroll',
    (event) => {
      if (event.target instanceof Element && event.target.matches(TRACK)) scheduleUpdate();
    },
    { capture: true, passive: true }
  );

  // Track size changes when the drawer opens (0 -> visible width) or the window
  // resizes; re-observe new tracks after each Section Rendering morph.
  const resizeObserver = new ResizeObserver(scheduleUpdate);
  /** @type {WeakSet<Element>} */
  const observed = new WeakSet();
  const observeTracks = () => {
    for (const track of document.querySelectorAll(TRACK)) {
      if (observed.has(track)) continue;
      observed.add(track);
      resizeObserver.observe(track);
    }
    scheduleUpdate();
  };

  const drawer = document.getElementById('cart-drawer');
  if (drawer) {
    new MutationObserver(observeTracks).observe(drawer, { childList: true, subtree: true });
  }
  observeTracks();
}
